import { ELEVATED_LABEL_SUFFIX } from './complexLoginIdentities';
import {
  type ApiKeyFormData,
  type BasicCredentialFormData,
  type BearerTokenFormData,
  type ComplexLoginFormData,
  type ComplexLoginMultiFormData,
  type CredentialIdentity,
  type DynamicConfigState,
  type RecordingCredentialFormData,
  type StaticTokenFormData,
} from './types';

export function validateBasicFormData(
  form: BasicCredentialFormData
): string | null {
  if (!form.username || !form.password) {
    return 'Username and password are required';
  }
  return null;
}

export function validateRecordingFormData(
  form: RecordingCredentialFormData
): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (!form.file) {
    return 'Please select a recording file';
  }
  return null;
}

export function validateDynamicConfig(
  config: DynamicConfigState
): string | null {
  if (!config.tokenServiceUrl) {
    return 'Token Service URL is required';
  }
  if (!config.tokenLocation) {
    return 'Token Location is required';
  }
  return null;
}

export function validateBearerTokenFormData(
  form: BearerTokenFormData
): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (form.isDynamic) {
    return validateDynamicConfig(form.dynamicConfig);
  } else {
    if (!form.token) {
      return 'Token is required';
    }
  }
  return null;
}

export function validateApiKeyFormData(form: ApiKeyFormData): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (!form.parameterName) {
    return 'Parameter Name is required';
  }
  if (form.isDynamic) {
    return validateDynamicConfig(form.dynamicConfig);
  } else {
    if (!form.apiKey) {
      return 'API Key is required';
    }
  }
  return null;
}

export function validateStaticTokenFormData(
  form: StaticTokenFormData
): string | null {
  const nonEmpty = form.headers.filter(h => h.key && h.value);
  if (nonEmpty.length === 0) {
    return 'At least one header with key and value is required';
  }
  return null;
}

/**
 * validateComplexLoginBasics checks only the basics-screen fields (label +
 * login URL). Split out of {@link validateComplexLoginFormData} so the
 * two-screen Complex-Login flow can validate the first screen before advancing
 * to the Login Inputs screen, keeping the label/URL rules single-sourced.
 */
export function validateComplexLoginBasics(
  form: ComplexLoginFormData
): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (!form.loginUrl.trim()) {
    return 'Login URL is required';
  }
  // Single-source the scheme rule via validateHttpUrl (rejects non-http(s)).
  // Map its generic message to the basics-screen wording.
  if (validateHttpUrl(form.loginUrl) !== null) {
    return 'Login URL must be a valid URL (including https://)';
  }
  return null;
}

export function validateComplexLoginFormData(
  form: ComplexLoginFormData
): string | null {
  const basicsError = validateComplexLoginBasics(form);
  if (basicsError) {
    return basicsError;
  }
  const nonEmpty = form.inputs.filter(i => i.key.trim() && i.value);
  if (nonEmpty.length === 0) {
    return 'At least one input with key and value is required';
  }
  return null;
}

// =============================================================================
// Header + URL validators (security P1-4)
// =============================================================================

// RFC 7230 token charset for HTTP header field names.
const HEADER_KEY_TOKEN = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/;

/**
 * validateHeaderKey rejects header names containing characters outside the
 * RFC 7230 `token` production. Returns an error string, or null when valid.
 */
export function validateHeaderKey(key: string): string | null {
  if (!key) {
    return 'Header name is required';
  }
  if (!HEADER_KEY_TOKEN.test(key)) {
    return 'Header name contains invalid characters';
  }
  return null;
}

/**
 * validateHeaderValue rejects header values containing CR, LF, or other
 * control characters (header-injection guard). Returns an error string, or
 * null when valid.
 */
export function validateHeaderValue(value: string): string | null {
  // Reject CR, LF, and any C0/DEL control characters (header-injection guard).
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(value)) {
    return 'Header value contains invalid characters';
  }
  return null;
}

/**
 * validateHttpUrl rejects values that are not parseable URLs or whose scheme
 * is not http/https. Returns an error string, or null when valid.
 */
export function validateHttpUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'Must be a valid URL (including https://)';
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'URL must use http or https';
  }
  return null;
}

// =============================================================================
// Multi-credential-set Complex Login (identities)
// =============================================================================

// Per-identity error shape. Each field is set only when that field fails.
// `null` from validateIdentity means the identity is fully valid.
export interface IdentityError {
  id: string;
  label?: string; // 'Label is required' / 'Duplicate label'
  loginUrl?: string; // 'Login URL is required' / validateHttpUrl message
  inputs?: string; // 'Username and password are required'
  mfa?: string; // 'Invalid TOTP secret'
}

export interface MultiValidationResult {
  ok: boolean; // every identity valid AND >=1 identity
  formError?: string; // form-level: 'Add at least one identity'
  identityErrors: IdentityError[]; // per-identity, indexed by identity.id
  readyCount: number; // for "N of M ready"
}

// Defensive base32 shape test (RFC 4648, no padding) for an already-normalized
// TOTP secret. Used only when totpSecret is non-empty (empty = no MFA = valid).
const BASE32_SECRET = /^[A-Z2-7]+$/i;

/**
 * Per-identity validation. Validates the identity's OWN loginUrl (via
 * {@link validateHttpUrl}), non-empty + non-duplicate label (`allLabels` is the
 * full list of identity labels for duplicate detection), the
 * username+password input rule, and a defensive base32 check on `totpSecret`
 * only when it is set. Returns `null` when the identity is fully valid.
 */
export function validateIdentity(
  identity: CredentialIdentity,
  allLabels: string[]
): IdentityError | null {
  const error: IdentityError = { id: identity.id };
  let hasError = false;

  // loginUrl — required + must be a valid http/https URL.
  if (!identity.loginUrl.trim()) {
    error.loginUrl = 'Login URL is required';
    hasError = true;
  } else {
    const urlError = validateHttpUrl(identity.loginUrl);
    if (urlError) {
      error.loginUrl = urlError;
      hasError = true;
    }
  }

  // label — non-empty + not a duplicate of another identity's label.
  const label = identity.label.trim();
  if (!label) {
    error.label = 'Label is required';
    hasError = true;
  } else if (label.endsWith(ELEVATED_LABEL_SUFFIX)) {
    error.label = `Label cannot end with "${ELEVATED_LABEL_SUFFIX.trim()}" (reserved)`;
    hasError = true;
  } else {
    const duplicates = allLabels.filter(l => l.trim() === label).length;
    if (duplicates > 1) {
      error.label = 'Duplicate label';
      hasError = true;
    }
  }

  // inputs — must contain non-empty username AND password values.
  const username = (
    identity.inputs.find(i => i.key === 'username')?.value ?? ''
  ).trim();
  const password = (
    identity.inputs.find(i => i.key === 'password')?.value ?? ''
  ).trim();
  if (!username || !password) {
    error.inputs = 'Username and password are required';
    hasError = true;
  }

  // mfa — defensive base32 check only when a secret is set (empty = valid).
  if (identity.totpSecret && !BASE32_SECRET.test(identity.totpSecret)) {
    error.mfa = 'Invalid TOTP secret';
    hasError = true;
  }

  return hasError ? error : null;
}

/**
 * Whole-form validation. Requires >=1 identity, runs {@link validateIdentity}
 * per identity (passing the full label list for duplicate detection), counts
 * the ready identities, and is `ok` only when there is at least one identity and
 * every identity is valid. There is NO form-level loginUrl check — loginUrl is
 * per-identity.
 */
export function validateComplexLoginMulti(
  form: ComplexLoginMultiFormData
): MultiValidationResult {
  if (form.identities.length === 0) {
    return {
      ok: false,
      formError: 'Add at least one identity',
      identityErrors: [],
      readyCount: 0,
    };
  }

  const allLabels = form.identities.map(i => i.label);
  const identityErrors: IdentityError[] = [];
  let readyCount = 0;

  for (const identity of form.identities) {
    const error = validateIdentity(identity, allLabels);
    if (error) {
      identityErrors.push(error);
    } else {
      readyCount += 1;
    }
  }

  return {
    ok: identityErrors.length === 0,
    identityErrors,
    readyCount,
  };
}
